import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSetAtom } from 'jotai'
import { useAtomCallback } from 'jotai/utils'
import { useQueryClient } from '@tanstack/react-query'
import {
  useAgents,
  useConversation,
  useCreateConversation,
  statsAssistantKeys,
} from '../../queries/statsAssistant'
import {
  DEFAULT_AGENT_PROFILE,
  isOptimisticMessage,
  StatsConversationDetail,
} from '../../../model/StatsAssistant'
import {
  patchTurn,
  statsAssistantPanelOpenAtom,
  statsAssistantProcessingUlidsAtom,
  statsAssistantTurnsAtom,
} from '../../../state/statsAssistant'
import { useStatsAssistantTurn } from './useStatsAssistantTurn.hook'
import { titleFromFirstQuestion } from '../../../components/statsAssistant/utils/conversationTitle.utils'
import { findLastUserMessage } from '../../../components/statsAssistant/utils/groupMessages'
import { ChatThreadScrollSnapshot } from '../../../components/statsAssistant/ChatThread/ChatThread.model'

export type SendUserMessageOptions = {
  replaceFromMessageUlid?: string
}

/**
 * État d'interface du chat (conversation active, panneaux, édition). L'état des analyses en cours
 * n'en fait pas partie : les composants le lisent via useConversationTurn, pour que les events
 * Centrifugo ne re-rendent pas tout l'arbre.
 */
export const useStatsAssistantChat = () => {
  const queryClient = useQueryClient()
  const { mutateAsync: createConversation } = useCreateConversation()
  const agentsQuery = useAgents()
  const setPanelOpen = useSetAtom(statsAssistantPanelOpenAtom)
  const { run: runTurn, cancel: cancelConversationTurn } = useStatsAssistantTurn()

  const [conversationUlid, setConversationUlid] = useState<string | null>(null)
  const [agentProfile, setAgentProfile] = useState<string>(DEFAULT_AGENT_PROFILE)
  const [isHistoryOpen, setIsHistoryOpen] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [replaceFromMessageUlid, setReplaceFromMessageUlid] = useState<string | null>(null)

  // Le panneau compact, la modale et le plein écran montent chacun leur propre fil et leur propre
  // champ : brouillons et position de lecture vivent ici pour survivre au changement de taille.
  const draftsRef = useRef<Record<string, string>>({})
  const threadScrollRef = useRef<ChatThreadScrollSnapshot | null>(null)
  const getDraft = useCallback((key: string) => draftsRef.current[key], [])
  const setDraft = useCallback((key: string, value: string) => {
    if (value) draftsRef.current[key] = value
    else delete draftsRef.current[key]
  }, [])

  const conversationQuery = useConversation({ ulid: conversationUlid })
  // placeholderData garde la conversation précédente pendant le chargement : on ne l'affiche pas.
  const isConversationLoading =
    conversationUlid !== null &&
    (conversationQuery.isPending || conversationQuery.isPlaceholderData)
  const conversation =
    conversationUlid && !isConversationLoading ? conversationQuery.data : undefined
  const conversationMessages = conversation?.messages
  const messages = useMemo(() => conversationMessages ?? [], [conversationMessages])
  const defaultProfile = agentsQuery.data?.defaultProfile
  const conversationAgentProfile = conversation?.agentProfile
  const isConversationProcessing = conversation?.isProcessing

  useEffect(() => {
    const profile = conversationUlid ? conversationAgentProfile : defaultProfile
    if (profile) setAgentProfile(profile)
  }, [conversationAgentProfile, conversationUlid, defaultProfile])

  useEffect(() => {
    if (!isExpanded) setIsFullscreen(false)
  }, [isExpanded])

  // Page rechargée ou conversation rouverte pendant un tour : l'état du back fait foi.
  // Lu au moment du fetch uniquement, pour ne pas écraser le tour optimiste qui démarre.
  const syncTurnWithConversation = useAtomCallback(
    useCallback((get, set, ulid: string, isProcessing: boolean) => {
      if (get(statsAssistantProcessingUlidsAtom).includes(ulid) === isProcessing) return
      set(statsAssistantTurnsAtom, (turns) =>
        patchTurn(turns, ulid, { isProcessing, progressText: null }),
      )
    }, []),
  )

  useEffect(() => {
    if (!conversationUlid || isConversationProcessing === undefined) return
    syncTurnWithConversation(conversationUlid, isConversationProcessing)
  }, [conversationUlid, isConversationProcessing, syncTurnWithConversation])

  const selectConversation = useCallback((ulid: string) => {
    setConversationUlid(ulid)
    setIsHistoryOpen(false)
    setReplaceFromMessageUlid(null)
  }, [])

  const startNewConversation = useCallback(() => {
    setConversationUlid(null)
    setAgentProfile(defaultProfile ?? DEFAULT_AGENT_PROFILE)
    setIsHistoryOpen(false)
    setReplaceFromMessageUlid(null)
  }, [defaultProfile])

  const closeExpanded = useCallback(
    (keepCompactPanelOpen: boolean) => {
      setIsExpanded(false)
      setIsFullscreen(false)
      setIsHistoryOpen(false)
      setPanelOpen(keepCompactPanelOpen)
    },
    [setPanelOpen],
  )

  const collapseAssistant = useCallback(() => closeExpanded(false), [closeExpanded])
  const reduceToCompactPanel = useCallback(() => closeExpanded(true), [closeExpanded])

  const ensureConversation = useCallback(
    async (question: string): Promise<string> => {
      if (conversationUlid) return conversationUlid

      // Profils pas encore chargés : le back applique le profil par défaut de la spécialité.
      const isKnownProfile = agentsQuery.data?.items.some(({ key }) => key === agentProfile)
      const created = await createConversation({
        agentProfile: isKnownProfile ? agentProfile : undefined,
        title: titleFromFirstQuestion(question),
      })
      queryClient.setQueryData<StatsConversationDetail>(
        statsAssistantKeys.conversation(created.ulid),
        { ...created, isProcessing: false, messages: [] },
      )
      setConversationUlid(created.ulid)
      return created.ulid
    },
    [agentProfile, agentsQuery.data, conversationUlid, createConversation, queryClient],
  )

  const sendUserMessage = useCallback(
    async (message: string, options: SendUserMessageOptions = {}): Promise<boolean> => {
      const trimmed = message.trim()
      if (!trimmed) return false

      let activeUlid: string
      try {
        activeUlid = await ensureConversation(trimmed)
      } catch {
        // L'erreur est notifiée par le MutationCache global ; l'appelant rend la question à l'utilisateur.
        return false
      }

      setReplaceFromMessageUlid(null)

      runTurn({
        conversationUlid: activeUlid,
        message: trimmed,
        replaceFromMessageUlid:
          options.replaceFromMessageUlid ?? replaceFromMessageUlid ?? undefined,
      })
      return true
    },
    [ensureConversation, replaceFromMessageUlid, runTurn],
  )

  const cancelTurn = useCallback(() => {
    if (conversationUlid) cancelConversationTurn(conversationUlid)
  }, [cancelConversationTurn, conversationUlid])

  const retryLastTurn = useCallback(() => {
    const lastUser = findLastUserMessage(messages)
    if (!lastUser || isOptimisticMessage(lastUser)) return
    void sendUserMessage(lastUser.content.text, { replaceFromMessageUlid: lastUser.ulid })
  }, [messages, sendUserMessage])

  // Lu à la demande : l'état des tours n'est pas suivi ici (cf. commentaire du hook).
  const readFailedRequest = useAtomCallback(
    useCallback(
      (get, _set, ulid: string) => get(statsAssistantTurnsAtom)[ulid]?.failedRequest ?? null,
      [],
    ),
  )

  /**
   * « Réessayer » de la bannière d'erreur. Envoi refusé : on rejoue la requête refusée, absente du
   * fil. Échec signalé par le back en fin de tour : la question est en base, on relance la dernière.
   */
  const retryTurnError = useCallback(() => {
    const failed = conversationUlid ? readFailedRequest(conversationUlid) : null
    if (!failed) {
      retryLastTurn()
      return
    }
    void sendUserMessage(failed.message, {
      replaceFromMessageUlid: failed.replaceFromMessageUlid,
    })
  }, [conversationUlid, readFailedRequest, retryLastTurn, sendUserMessage])

  const startEditUserMessage = useCallback((ulid: string) => setReplaceFromMessageUlid(ulid), [])

  const cancelEdit = useCallback(() => setReplaceFromMessageUlid(null), [])

  const title = conversation?.title ?? (messages.length > 0 ? 'Statistiques' : 'Nouvelle analyse')
  const agentProfiles = agentsQuery.data?.items

  return useMemo(
    () => ({
      conversationUlid,
      isConversationLoading,
      title,
      agentProfile,
      agentProfiles: agentProfiles ?? [],
      setAgentProfile,
      messages,
      isHistoryOpen,
      setIsHistoryOpen,
      isExpanded,
      setIsExpanded,
      isFullscreen,
      setIsFullscreen,
      editingMessageUlid: replaceFromMessageUlid,
      getDraft,
      setDraft,
      threadScrollRef,
      selectConversation,
      startNewConversation,
      sendUserMessage,
      cancelTurn,
      retryLastTurn,
      retryTurnError,
      startEditUserMessage,
      cancelEdit,
      collapseAssistant,
      reduceToCompactPanel,
    }),
    [
      agentProfile,
      agentProfiles,
      cancelEdit,
      collapseAssistant,
      conversationUlid,
      getDraft,
      isConversationLoading,
      isExpanded,
      isFullscreen,
      isHistoryOpen,
      messages,
      reduceToCompactPanel,
      replaceFromMessageUlid,
      retryLastTurn,
      retryTurnError,
      selectConversation,
      sendUserMessage,
      setDraft,
      startEditUserMessage,
      startNewConversation,
      cancelTurn,
      title,
    ],
  )
}

export type StatsAssistantChatState = ReturnType<typeof useStatsAssistantChat>
